import React, { useRef, useEffect, useState, useMemo, useCallback, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Portal from '@ui/portal/portal';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import Button from '@ui/buttons/button/button';
import { ICONS } from '@ui/icons/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getWorkingProject,
  selectProjectThemePack,
  selectProjectThemeMode,
  selectWorkingProject,
  selectWorkingProjectUuid,
} from '@/store/slices/project/selectors';
import {
  loadEventScheduledEmails,
  patchScheduledEmailCustomizations,
  patchScheduledEmailFields,
  updateScheduledEmail,
} from '@/store/slices/scheduled-emails';
import { EmailTypeEnum, type IScheduledEmailContentV2 } from '@/types/email.types';
import type { DateRange } from '@ui/calendar/types';
import { getUserTimezone } from '@ui/calendar/utils';
import {
  selectScheduledEmailByUuid,
  selectLoadingScheduledEmails,
  selectUpdatingEmail,
  selectErrorLoadingScheduledEmails,
} from '@/store/slices/scheduled-emails/selectors';
import { getEmailEditorFields, scheduledEmailToCampaign } from '@/utils/scheduled-email-campaign';
import { campaignTemplatesMap } from '../templates';
import classes from './campaign-editor.module.css';
import PillTabs from '@ui/tabs/pill-tabs';
import classNames from 'classnames';
import { PreviewSize, deviceMaxWidths, TEMPLATE_WIDTH, fullScreenStyle } from './constants';
import { useLoadAllAttendeesList } from '../../audiences/hooks/listsv2/use-load-all-attendees-list';
import { SendTestEmail } from '@/connection/scheduled-emails/scheduled-emails';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { selectUser } from '@/store/slices/user/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import CampaignEditorTextInputs from './components/text-inputs';
import DateInputs from './components/date-inputs';
import ColorInputs from './components/color-inputs';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { selectFavoriteTemplates } from '@/store/slices/campaigns/selectors';
import { addFavoriteTemplate, loadFavoriteTemplates, removeFavoriteTemplate } from '@/store/slices/campaigns';
import AudienceInput from './components/audience-input';
import CampaignDataInputs from './components/campaign-data-inputs';
import LanguageSwitcher from './components/language-switcher';

export default function CampaignEditor() {
  const { campaignUuid, projectUuid, language } = useParams<{
    campaignUuid: string;
    projectUuid: string;
    language: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  useLoadAllAttendeesList();

  const workingProject = useAppSelector(selectWorkingProject);
  const projectUuidFromStore = useAppSelector(selectWorkingProjectUuid);
  const errorLoadingScheduledEmails = useAppSelector(selectErrorLoadingScheduledEmails);
  const loadingEmails = useAppSelector(selectLoadingScheduledEmails);
  const favoriteTemplates = useAppSelector(selectFavoriteTemplates);
  const event = useAppSelector(getWorkingProject);
  const theme = useAppSelector(selectProjectThemePack);
  const themeMode = useAppSelector(selectProjectThemeMode);
  const user = useAppSelector(selectUser);
  const emailSelector = useMemo(() => selectScheduledEmailByUuid(campaignUuid), [campaignUuid]);
  const scheduledEmail = useAppSelector(emailSelector);
  const templateId = useMemo(() => {
    return String(scheduledEmail?.customizations?.v2?.templateId);
  }, [scheduledEmail?.customizations?.v2?.templateId]);

  const isFavorited = useMemo(
    () => templateId && favoriteTemplates.includes(templateId as string),
    [templateId, favoriteTemplates]
  );
  const [previewSize, setPreviewSize] = useState<PreviewSize>('desktop');
  const [showGlobalStyles, setShowGlobalStyles] = useState(true);
  const [tab, setTab] = useState<'Edit' | 'Preview'>('Edit');
  const [headingFont, setHeadingFont] = useState('Arial');
  const [bodyFont, setBodyFont] = useState('Arial');
  const [salutation, setSalutation] = useState('');
  const [title, setTitle] = useState('');
  const [startIntro, setStartIntro] = useState('');
  const [cta, setCta] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [headingImage, setHeadingImage] = useState('');
  const [description, setDescription] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<number | null>(null);
  const [backgroundColor, setBackgroundColor] = useState('#fff');
  const [containerColor, setContainerColor] = useState('#fff');
  const [bodyTextColor, setBodyTextColor] = useState('#fff');
  const [headingTextColor, setHeadingTextColor] = useState('#fff');
  const [accentColor, setAccentColor] = useState('#fff');
  const [secondaryAccentColor, setSecondaryAccentColor] = useState('#fff');
  const [secondaryBackgroundColor, setSecondaryBackgroundColor] = useState('#fff');
  const [buttonTextColor, setButtonTextColor] = useState('#fff');
  const [timezone, setTimezone] = useState<string>(getUserTimezone());
  const [scale, setScale] = useState(1);
  const [sendingTest, setSendingTest] = useState(false);

  const templateContainer = useRef<HTMLDivElement | null>(null);
  const initializedFromEmail = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const reduxTimeout = useRef<NodeJS.Timeout | null>(null);
  const baseLanguage = workingProject?.settings.i18n?.base ?? 'en';

  const campaign = useMemo(() => (scheduledEmail ? scheduledEmailToCampaign(scheduledEmail) : null), [scheduledEmail]);
  const showDeliveryDate = scheduledEmail?.email_type !== EmailTypeEnum.event_registration_email;

  useEffect(() => {
    if (!scheduledEmail && !loadingEmails && projectUuidFromStore && !errorLoadingScheduledEmails) {
      dispatch(loadEventScheduledEmails(projectUuidFromStore));
    }
  }, [scheduledEmail, loadingEmails, projectUuidFromStore, dispatch, errorLoadingScheduledEmails]);

  useEffect(() => {
    initializedFromEmail.current = false;
  }, [language]);

  // Populate editor fields from the scheduled email the first time it loads.
  useEffect(() => {
    if (scheduledEmail && !initializedFromEmail.current && language) {
      const fields = getEmailEditorFields(scheduledEmail);
      setTitle((fields.title[language] as string) || fields.title.base || workingProject?.name || '');
      setDescription((fields.description[language] as string) || fields.description.base);
      setHeadingFont(fields.headingFont);
      setBodyFont(fields.bodyFont);
      setSalutation((fields.salutation[language] as string) || fields.salutation.base);
      setStartIntro((fields.startIntro[language] as string) || fields.startIntro.base);
      setCta((fields.cta[language] as string) || fields.cta.base);
      setHeadingImage(fields.headingImage);
      setMainImage(fields.mainImage);
      setDeliveryDate(scheduledEmail.delivery_date || null);
      setTimezone(scheduledEmail.timezone || getUserTimezone());
      setBackgroundColor(fields.backgroundColor ?? '#fff');
      setContainerColor(fields.containerColor ?? '#eee');
      setAccentColor(fields.accentColor ?? '#ddd');
      setHeadingTextColor(fields.headingTextColor ?? '#000');
      setBodyTextColor(fields.bodyTextColor ?? '#111');
      setSecondaryAccentColor(fields.secondaryAccentColor ?? '#222');
      setSecondaryBackgroundColor(fields.secondaryBackgroundColor ?? '#ccc');
      setButtonTextColor(fields.buttonTextColor ?? '#444');
      initializedFromEmail.current = true;
    }
  }, [scheduledEmail, language, workingProject?.name]);

  const content = scheduledEmail?.customizations.v2 as IScheduledEmailContentV2 | undefined;

  const contentRef = useRef(content);
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  // Sync field changes back into the scheduled email's working_customizations in the store.
  useEffect(() => {
    const content = contentRef.current;
    if (!initializedFromEmail.current || !campaignUuid || !templateContainer.current || !content) {
      return;
    }
    if (reduxTimeout.current) {
      clearTimeout(reduxTimeout.current);
    }

    reduxTimeout.current = setTimeout(() => {
      dispatch(
        patchScheduledEmailCustomizations({
          uuid: campaignUuid,
          fields: {
            title: updateTranslateKey({
              translateString: content.title,
              input: title,
              baseLanguage,
              language: language ?? null,
            }),
            description: updateTranslateKey({
              translateString: content.description,
              input: description,
              baseLanguage,
              language: language ?? null,
            }),
            headingFont,
            bodyFont,
            salutation: updateTranslateKey({
              translateString: content.salutation,
              input: salutation,
              baseLanguage,
              language: language ?? null,
            }),
            startIntro: updateTranslateKey({
              translateString: content.startIntro,
              input: startIntro,
              baseLanguage,
              language: language ?? null,
            }),
            cta: updateTranslateKey({
              translateString: content.cta,
              input: cta,
              baseLanguage,
              language: language ?? null,
            }),
            mainImage,
            headingImage,
            backgroundColor,
            containerColor,
            secondaryAccentColor,
            secondaryBackgroundColor,
            accentColor,
            headingTextColor,
            bodyTextColor,
            buttonTextColor,
          },
        })
      );
    }, 750);
  }, [
    title,
    description,
    headingFont,
    bodyFont,
    salutation,
    startIntro,
    cta,
    mainImage,
    headingImage,
    campaignUuid,
    language,
    dispatch,
    backgroundColor,
    containerColor,
    secondaryAccentColor,
    secondaryBackgroundColor,
    accentColor,
    headingTextColor,
    bodyTextColor,
    buttonTextColor,
    baseLanguage,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      const available = entry.contentRect.width;
      const max = deviceMaxWidths[previewSize];
      setScale(Math.min(1, Math.min(available, max) / TEMPLATE_WIDTH));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [previewSize]);

  useEffect(() => {
    dispatch(loadFavoriteTemplates());
  }, [dispatch]);

  const updatingEmail = useAppSelector(selectUpdatingEmail);

  const TemplateComponent = useMemo(() => {
    if (scheduledEmail?.working_customizations?.v2.templateId) {
      return campaignTemplatesMap.get(scheduledEmail.working_customizations?.v2.templateId as string);
    }

    return campaign?.template_id ? campaignTemplatesMap.get(campaign.template_id) : undefined;
  }, [campaign?.template_id, scheduledEmail?.working_customizations?.v2.templateId]);

  const handleBack = useCallback(() => {
    navigate(`/project/edit/${projectUuid}/${language}/campaigns`);
  }, [language, navigate, projectUuid]);

  const handleDeliveryDateChange = useCallback(
    (range: DateRange) => {
      const ts = range.startDate;
      setDeliveryDate(ts);
      if (campaignUuid) {
        dispatch(patchScheduledEmailFields({ uuid: campaignUuid, fields: { delivery_date: ts ?? 0 } }));
      }
    },
    [campaignUuid, dispatch]
  );

  const handleTimezoneChange = useCallback(
    (tz: string) => {
      setTimezone(tz);
      if (campaignUuid) {
        dispatch(patchScheduledEmailFields({ uuid: campaignUuid, fields: { timezone: tz } }));
      }
    },
    [campaignUuid, dispatch]
  );

  const getFinal = useCallback(() => {
    if (!scheduledEmail) return;

    const customizations = {
      v2: {
        ...scheduledEmail.customizations.v2,
      },
    };

    const working_customizations = {
      ...(scheduledEmail.working_customizations ?? scheduledEmail.customizations),
      v2: {
        ...(scheduledEmail.working_customizations?.v2 ?? scheduledEmail.customizations.v2),
      },
    };

    return {
      ...scheduledEmail,
      customizations: {
        ...customizations,
        ...working_customizations,
      },
      settings: {
        ...(scheduledEmail.settings ?? {}),
        allInvites: scheduledEmail.email_type !== EmailTypeEnum.event_registration_email,
      },
    };
  }, [scheduledEmail]);

  const handlePublish = useCallback(async () => {
    const emailToSave = getFinal();

    if (emailToSave) {
      const result = await dispatch(updateScheduledEmail(emailToSave));

      if (emailToSave.email_type === EmailTypeEnum.event_registration_email && workingProject?.registration_settings) {
        await dispatch(
          saveWorkingProjectByFields({
            registration_settings: {
              ...workingProject.registration_settings,
              registration_confirmation_email: emailToSave.customizations,
            },
          })
        );
      }

      if (emailToSave.sent) {
        toastTrigger('Already sent', {
          description:
            'The email was saved but it has already been sent and cannot be resent. Please create a new campaign.',
          type: 'warning',
        });
      }

      if (updateScheduledEmail.fulfilled.match(result)) {
        handleBack();
      }
    }
  }, [dispatch, getFinal, handleBack, workingProject?.registration_settings]);

  const sendTest = useCallback(async () => {
    const email = getFinal();
    if (!email || !user || !workingProject) return;
    try {
      setSendingTest(true);
      await SendTestEmail([user.email], email, workingProject.uuid, workingProject.channel);
      toastTrigger('Test sent', {
        description: `Test email sent to ${user.email}`,
      });
    } catch (e) {
      console.error(e);
      toastTrigger('Error sending test', {
        description: 'We ran into an issue sending that email. Please try again later.',
        type: 'error',
      });
    } finally {
      setSendingTest(false);
    }
  }, [getFinal, user, workingProject]);

  return (
    <Portal style={fullScreenStyle}>
      <div className={classes.wrapper}>
        {/* ── Header ── */}
        <header className={classes.header}>
          <div className={classes.headerLeft}>
            <button className={classes.backButton} onClick={handleBack}>
              <Icon name={ICONS.ARROW_LEFT} size={20} color="var(--global-ico-text-2)" />
            </button>
            <Text className="H4" color="var(--global-ico-text-1)">
              {campaign?.title || 'Campaign'}
            </Text>
          </div>

          <div className="flex-1"></div>
          <div className={classes.headerCenter}>
            <PillTabs tabs={['Edit', 'Preview']} value={tab} onChange={setTab} />
          </div>

          <div className={classes.headerRight}>
            <Button loading={sendingTest} onClick={sendTest} designSize="small" design="LocalBttnSecHalfTransp">
              <Text className="C2 flex ai-center gap-6">
                Send test <Icon name={ICONS.EMAIL_SEND} size={12} />
              </Text>
            </Button>
            <div className={classes.deviceToggleGroup}>
              <Button
                clear
                className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'desktop' })}
                onClick={() => setPreviewSize('desktop')}
                title="Desktop"
              >
                <Icon name={ICONS.DESKTOP} size={16} color="var(--global-ico-text-2)" />
              </Button>
              <Button
                clear
                className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'tablet' })}
                onClick={() => setPreviewSize('tablet')}
                title="Tablet"
              >
                <Icon name={ICONS.TABLET} size={15} color="var(--global-ico-text-2)" />
              </Button>
              <Button
                clear
                className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'mobile' })}
                onClick={() => setPreviewSize('mobile')}
                title="Mobile"
              >
                <Icon name={ICONS.MOBILE} size={13} color="var(--global-ico-text-2)" />
              </Button>
            </div>

            <Button
              clear
              onClick={() => {
                if (!templateId) return;

                if (isFavorited) {
                  dispatch(removeFavoriteTemplate(templateId));
                } else {
                  dispatch(addFavoriteTemplate(templateId));
                }
              }}
              className="square-40 flex ai-center"
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Icon
                name={isFavorited ? ICONS.HEART_FILLED : ICONS.HEART_FILLED_OUTLINE}
                size={20}
                color={isFavorited ? 'var(--primitives-mode-1-pink-400)' : 'var(--global-ico-text-2)'}
              />
            </Button>

            <Button
              className="L1"
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              borderRadius="100px"
              padding="10px 20px"
              onClick={handlePublish}
              disabled={updatingEmail || !scheduledEmail}
              loading={updatingEmail}
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1 flex ai-center gap-8"
              >
                Publish
                <Icon name={ICONS.ARROW_RIGHT} size={16} />
              </Text>
            </Button>
          </div>
        </header>

        {/* ── Body ── */}
        <div className={classNames(classes.body, { [classes.preview]: tab === 'Preview' })}>
          {/* Left panel — Global Styles */}
          {showGlobalStyles && (
            <div className={classNames(classes.panelWrapper, classes.panelLeft)}>
              <aside className={classes.panel}>
                <div className={classes.panelHeader}>
                  <div className="flex ai-center gap-8">
                    <Icon name={ICONS.PALETTE} size={16} color="var(--global-ico-text-2)" />
                    <Text className="L1" color="var(--global-ico-text-1)">
                      Details
                    </Text>
                  </div>
                  <button className={classes.iconButton} onClick={() => setShowGlobalStyles(false)}>
                    <Icon name={ICONS.CLOSE} size={14} color="var(--global-ico-text-3)" />
                  </button>
                </div>
                <div className={classes.panelContent}>
                  {scheduledEmail?.email_type !== EmailTypeEnum.event_registration_email && (
                    <AudienceInput scheduledEmail={scheduledEmail} />
                  )}

                  <LanguageSwitcher />
                  <CampaignDataInputs scheduledEmail={scheduledEmail} />

                  {showDeliveryDate && (
                    <DateInputs
                      timezone={timezone}
                      deliveryDate={deliveryDate}
                      handleDeliveryDateChange={handleDeliveryDateChange}
                      handleTimezoneChange={handleTimezoneChange}
                    />
                  )}

                  <CampaignEditorTextInputs
                    headingFont={headingFont}
                    bodyFont={bodyFont}
                    setHeadingFont={setHeadingFont}
                    setBodyFont={setBodyFont}
                  />

                  <ColorInputs
                    backgroundColor={backgroundColor}
                    containerColor={containerColor}
                    accentColor={accentColor}
                    secondaryAccentColor={secondaryAccentColor}
                    secondaryBackgroundColor={secondaryBackgroundColor}
                    headingTextColor={headingTextColor}
                    bodyTextColor={bodyTextColor}
                    buttonTextColor={buttonTextColor}
                    setBackgroundColor={setBackgroundColor}
                    setContainerColor={setContainerColor}
                    setAccentColor={setAccentColor}
                    setSecondaryAccentColor={setSecondaryAccentColor}
                    setSecondaryBackgroundColor={setSecondaryBackgroundColor}
                    setHeadingTextColor={setHeadingTextColor}
                    setBodyTextColor={setBodyTextColor}
                    setButtonTextColor={setButtonTextColor}
                  />
                </div>
              </aside>
            </div>
          )}
          {/* Center — email preview */}
          <main className={classes.previewArea}>
            <div
              className={classes.previewScroll}
              ref={containerRef}
              style={{ '--preview-background': backgroundColor } as CSSProperties}
            >
              {event && theme && TemplateComponent ? (
                <div style={{ transform: `scale(${scale}`, transformOrigin: 'top center' }}>
                  <div style={{ width: TEMPLATE_WIDTH }} ref={templateContainer}>
                    <TemplateComponent
                      event={event}
                      theme={theme}
                      mode={themeMode ?? undefined}
                      href={`${import.meta.env.VITE_URL_SHORTENER}/${event.uuid}{{reg_code}}`}
                      salutation={salutation}
                      setSalutation={setSalutation}
                      title={title}
                      setTitle={setTitle}
                      cta={cta}
                      setCta={setCta}
                      description={description}
                      setDescription={setDescription}
                      startIntro={startIntro}
                      setStartIntro={setStartIntro}
                      setHeaderImage={setHeadingImage}
                      setMainImage={setMainImage}
                      customizations={{
                        title,
                        description,
                        headingFont,
                        bodyFont,
                        salutation,
                        startIntro,
                        cta,
                        mainImage,
                        headingImage,
                        backgroundColor,
                        containerColor,
                        secondaryAccentColor,
                        secondaryBackgroundColor,
                        accentColor,
                        headingTextColor,
                        bodyTextColor,
                        buttonTextColor,
                        secondaryImage: mainImage,
                        subject: title,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className={classes.noTemplate}>
                  <Text className="L1" design="GlobalIcoText3">
                    {TemplateComponent ? 'Loading preview...' : 'No template selected'}
                  </Text>
                </div>
              )}
            </div>
          </main>
          {/* <div className={classNames(classes.panelWrapper, classes.panelRight)}>
            <aside className={classes.panel}>
              <div className={classes.panelBody}>
                <div className={classes.panelHeader}>
                  <div className="flex ai-center gap-8">
                    <Icon name={ICONS.USERS_AUDIENCE} size={16} color="var(--global-ico-text-2)" />
                    <Text className="L1" color="var(--global-ico-text-1)">
                      Audiences
                    </Text>
                  </div>
                  <button className={classes.iconButton} onClick={() => setShowGlobalStyles(false)}>
                    <Icon name={ICONS.CLOSE} size={14} color="var(--global-ico-text-3)" />
                  </button>
                </div>
                <div className={classes.panelContent}>
                  <AudienceInput scheduledEmail={scheduledEmail} />
                  {showDeliveryDate && (
                    <DateInputs
                      timezone={timezone}
                      deliveryDate={deliveryDate}
                      handleDeliveryDateChange={handleDeliveryDateChange}
                      handleTimezoneChange={handleTimezoneChange}
                    />
                  )}
                </div>
              </div>
            </aside>
          </div> */}
        </div>
      </div>
    </Portal>
  );
}
